import { Outlet, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { isNative } from '@/lib/capacitor';

interface PublicLayoutProps {
  children?: React.ReactNode;
}

/**
 * PublicLayout - Minimal layout for public pages (legal, help, GDPR)
 *
 * Features:
 * - Back button linking to login page (native) or app.parentpath.de (web)
 * - No InfoCard or Notification containers
 * - Clean, minimal design for legal compliance pages
 * - Safe area support for native apps
 */
export function PublicLayout({ children }: PublicLayoutProps) {
  return (
    <div className="min-h-screen flex flex-col">
      {/* Public TopBar - Only back button */}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-pp-dark/95 backdrop-blur-sm border-b border-white/10"
        style={isNative ? { paddingTop: 'env(safe-area-inset-top, 0px)' } : undefined}
      >
        <div className="max-w-[767px] mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Back button - In native apps, navigate to /login; on web, go to external URL */}
            {isNative ? (
              <Link
                to="/login"
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Zurück zur App</span>
              </Link>
            ) : (
              <a
                href="https://app.parentpath.de"
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Zurück zur App</span>
              </a>
            )}

            {/* Logo - In native apps, go to login; on web, go to home */}
            <Link to={isNative ? '/login' : '/'} className="flex items-center">
              <img
                src="https://parentpath.de/pp_logo-piktogram-w100.svg"
                alt="ParentPath"
                className="w-8 h-8"
              />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className="flex-1 overflow-y-auto pt-14 pb-8"
        style={isNative ? { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' } : undefined}
      >
        <div className="max-w-[767px] mx-auto px-4 py-6">
          {children || <Outlet />}
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="border-t border-white/10 py-4">
        <div className="max-w-[767px] mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-white/50">
            <Link to="/legal/imprint" className="hover:text-white/80 transition-colors">
              Impressum
            </Link>
            <Link to="/legal/privacy" className="hover:text-white/80 transition-colors">
              Datenschutz
            </Link>
            <Link to="/legal/terms" className="hover:text-white/80 transition-colors">
              Nutzungsbedingungen
            </Link>
            <Link to="/help" className="hover:text-white/80 transition-colors">
              Hilfe
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
